import { Stack, Grid, Typography, GridProps, StackProps, GridSize } from '@mui/material';
import type { ReactNode } from 'react';

interface SplitPanelLayoutProps {
    /** Optional content displayed above the title (e.g., progress indicators, breadcrumbs) */
    headerContent?: ReactNode;
    /** Title displayed above the two-panel layout. Optional if title is included in leftContent. */
    title?: string;
    /** Content for the left panel (typically text, lists, or forms) */
    leftContent: ReactNode;
    /** Content for the right panel (typically illustrations or media). Shown below left content on mobile. */
    rightContent: ReactNode;
    /** Optional Props for the grid items */
    slotProps?: {
        stack?: StackProps;
        grid?: {
            container?: GridProps;
            left?: GridProps;
            right?: GridProps;
        };
    };
}

/**
 * A responsive two-panel layout component with customizable left and right content areas.
 * Displays a title above the content, with the left panel containing any React content
 * and the right panel typically showing illustrations or media. On mobile, collapses to
 * single column with right panel shown below left panel.
 */
export default function SplitPanelLayout({
    headerContent,
    title,
    leftContent,
    rightContent,
    slotProps,
}: Readonly<SplitPanelLayoutProps>) {
    return (
        <Stack
            justifyContent="center"
            alignItems="center"
            {...slotProps?.stack}
            sx={{
                minHeight: '100%',
                ...(slotProps?.stack?.sx || {}),
            }}>
            <Grid
                container
                {...slotProps?.grid?.container}
                sx={{ height: '100%', width: '100%', ...(slotProps?.grid?.container?.sx || {}) }}>
                {/* Left Panel - Content */}
                <Grid
                    {...slotProps?.grid?.left}
                    size={{
                        xs: 12,
                        md: 6,
                        ...(slotProps?.grid?.left?.size as Record<string, GridSize> | undefined),
                    }}
                    sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: { xs: 2, md: 6 },
                        ...(slotProps?.grid?.left?.sx || {}),
                    }}>
                    <Stack
                        spacing={4}
                        sx={{
                            p: { xs: 2, md: 4 },
                            backgroundColor: 'background.paper',
                            borderRadius: 2,
                            width: '100%',
                        }}>
                        {headerContent}
                        {title && <Typography variant="h1">{title}</Typography>}
                        {leftContent}
                    </Stack>
                </Grid>

                {/* Right Panel - Media/Illustration */}
                <Grid
                    {...slotProps?.grid?.right}
                    size={{ xs: 12, md: 6, ...(slotProps?.grid?.right?.size as Record<string, GridSize> | undefined) }}
                    sx={{
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        p: { xs: 2, md: 6 },
                        ...(slotProps?.grid?.right?.sx || {}),
                    }}>
                    {rightContent}
                </Grid>
            </Grid>
        </Stack>
    );
}
